/* eslint-disable no-restricted-imports */
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import emptyFunction from "fbjs/lib/emptyFunction";
import { getExternalAppName } from "environment";
import { DARK_THEME, PULSZ_APP } from "src/constants";
import { ThemeType } from "src/enums";
import { VoidCallback } from "src/types/common";
import usePersistentState from "ui/hooks/usePersistentState";
import styles from "./Theme.scss";

interface ThemeContext {
  dark: boolean;
  lockTheme: (type: ThemeType) => void;
  setDark: (value: boolean) => void;
  unLockTheme: VoidCallback;
}

export const themeLockClassnames: Record<ThemeType, string> = {
  [ThemeType.LIGHT]: styles.themeLight,
  [ThemeType.DARK]: styles.themeDark,
  [ThemeType.PULSZ]: styles.themePulsz,
};

export const ThemeContext = React.createContext<ThemeContext>({
  dark: true,
  setDark: emptyFunction,
  lockTheme: emptyFunction,
  unLockTheme: emptyFunction,
});

export const useThemeContext = () => useContext(ThemeContext);

export const ThemeProvider: React.FC = ({ children }) => {
  const [dark, setDark] = usePersistentState({
    key: DARK_THEME,
    initialValue: true,
  });
  const [lockedTheme, setLockTheme] = useState<ThemeType | null>(null);

  const isDarkTheme = useMemo(
    () => (!lockedTheme ? dark : lockedTheme === ThemeType.DARK),
    [lockedTheme, dark]
  );

  const setRootThemeClassname = useCallback((isDark: boolean) => {
    const isPulsz = getExternalAppName() === PULSZ_APP;

    if (isPulsz) {
      document.body.classList.add(
        themeLockClassnames.light,
        themeLockClassnames[PULSZ_APP as ThemeType]
      );
    } else {
      document.body.classList.remove(
        isDark ? themeLockClassnames.light : themeLockClassnames.dark
      );
      document.body.classList.add(
        isDark ? themeLockClassnames.dark : themeLockClassnames.light,
        styles.noTransition
      );
    }

    // preventing transitions on theme change
    window.getComputedStyle(document.body).opacity;
    document.body.classList.remove(styles.noTransition);
  }, []);

  const lockTheme = useCallback((type: ThemeType) => setLockTheme(type), []);
  const unLockTheme = useCallback(() => setLockTheme(null), []);

  const contextValues = useMemo(
    () => ({
      dark: isDarkTheme,
      setDark,
      lockTheme,
      unLockTheme,
    }),
    [dark, setDark, lockTheme, unLockTheme, isDarkTheme]
  );

  useEffect(() => {
    const themeColor = isDarkTheme ? "#141414" : "#ffffff";
    const statusBarStyle = isDarkTheme ? "black" : "default";

    let themeMetaTag = document.querySelector('meta[name="theme-color"]');
    if (!themeMetaTag) {
      themeMetaTag = document.createElement("meta");
      themeMetaTag.setAttribute("name", "theme-color");
      document.head.appendChild(themeMetaTag);
    }
    themeMetaTag.setAttribute("content", themeColor);

    let statusBarMetaTag = document.querySelector(
      'meta[name="apple-mobile-web-app-status-bar-style"]'
    );
    if (!statusBarMetaTag) {
      statusBarMetaTag = document.createElement("meta");
      statusBarMetaTag.setAttribute(
        "name",
        "apple-mobile-web-app-status-bar-style"
      );
      document.head.appendChild(statusBarMetaTag);
    }
    statusBarMetaTag.setAttribute("content", statusBarStyle);

    document.body.style.backgroundColor = themeColor;
    const header = document.querySelector(".header");
    if (header) {
      // @ts-ignore
      header.style.backgroundColor = themeColor;
    }
  }, [isDarkTheme]);

  useEffect(() => {
    setRootThemeClassname(isDarkTheme);
  }, [isDarkTheme, lockedTheme]);

  return (
    <ThemeContext.Provider value={contextValues}>
      {children}
    </ThemeContext.Provider>
  );
};
